<script setup lang="ts">
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AssistantTab from './tabs/AssistantTab.vue';
import CommentsTab from './tabs/CommentsTab.vue';
import NotesTab from './tabs/NotesTab.vue';
import ReferencesTab from './tabs/ReferencesTab.vue';
import TasksTab from './tabs/TasksTab.vue';
import type {
    DocumentCommentThreadItem,
    DocumentNoteItem,
    DocumentTaskItem,
} from './tabs/types';

defineProps<{
    references: ReferenceItem[];
    citedIds?: ReadonlySet<string>;
    collections?: string[];
    currentUserId: string;
    notes: DocumentNoteItem[];
    tasks: DocumentTaskItem[];
    commentThreads: DocumentCommentThreadItem[];
    pendingCommentAnchor: string | null;
    focusThreadId: string | null;
}>();

const activeTab = defineModel<string>('activeTab', { default: 'references' });

const emit = defineEmits<{
    cite: [ref: ReferenceItem];
    addNote: [body: string];
    removeNote: [id: string];
    addTask: [body: string];
    toggleTask: [id: string];
    removeTask: [id: string];
    createThread: [body: string];
    cancelPendingThread: [];
    replyThread: [threadId: string, body: string];
    resolveThread: [threadId: string];
    reopenThread: [threadId: string];
    removeThread: [threadId: string];
}>();

const tabs = [
    { value: 'references', label: 'Références' },
    { value: 'notes', label: 'Notes' },
    { value: 'comments', label: 'Commentaires' },
    { value: 'tasks', label: 'À faire' },
    { value: 'assistant', label: 'Assistant' },
] as const;
</script>

<template>
    <aside
        class="flex min-h-0 flex-col border-l border-border/40 bg-background"
    >
        <Tabs v-model="activeTab" class="flex min-h-0 flex-1 flex-col">
            <TabsList
                class="h-auto w-full shrink-0 gap-0 rounded-none border-b border-border/40 bg-background px-1 py-0"
            >
                <TabsTrigger
                    v-for="tab in tabs"
                    :key="tab.value"
                    :value="tab.value"
                    class="flex-1 rounded-none border-b-2 border-transparent px-1.5 py-3 text-[11px] whitespace-nowrap data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                    {{ tab.label }}
                </TabsTrigger>
            </TabsList>

            <TabsContent
                value="references"
                class="mt-0 flex min-h-0 flex-1 flex-col"
                data-tour="editor-citation"
            >
                <ReferencesTab
                    :references="references"
                    :cited-ids="citedIds"
                    :collections="collections"
                    @cite="emit('cite', $event)"
                />
            </TabsContent>

            <TabsContent value="notes" class="mt-0 min-h-0 flex-1">
                <ScrollArea class="h-full">
                    <NotesTab
                        :notes="notes"
                        :current-user-id="currentUserId"
                        @add="emit('addNote', $event)"
                        @remove="emit('removeNote', $event)"
                    />
                </ScrollArea>
            </TabsContent>

            <TabsContent value="comments" class="mt-0 min-h-0 flex-1">
                <ScrollArea class="h-full">
                    <CommentsTab
                        :threads="commentThreads"
                        :current-user-id="currentUserId"
                        :pending-anchor="pendingCommentAnchor"
                        :focus-thread-id="focusThreadId"
                        @create-thread="emit('createThread', $event)"
                        @cancel-pending="emit('cancelPendingThread')"
                        @reply="(id, body) => emit('replyThread', id, body)"
                        @resolve="emit('resolveThread', $event)"
                        @reopen="emit('reopenThread', $event)"
                        @remove="emit('removeThread', $event)"
                    />
                </ScrollArea>
            </TabsContent>

            <TabsContent value="tasks" class="mt-0 min-h-0 flex-1">
                <ScrollArea class="h-full">
                    <TasksTab
                        :tasks="tasks"
                        :current-user-id="currentUserId"
                        @add="emit('addTask', $event)"
                        @toggle="emit('toggleTask', $event)"
                        @remove="emit('removeTask', $event)"
                    />
                </ScrollArea>
            </TabsContent>

            <TabsContent value="assistant" class="mt-0 min-h-0 flex-1">
                <ScrollArea class="h-full">
                    <AssistantTab />
                </ScrollArea>
            </TabsContent>
        </Tabs>
    </aside>
</template>
